import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowLeftRight, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem } from '../bookings/util.js';
import ItemGroups from './ItemGroups.jsx';

// Office: Stock > Job card stock organizer. The items engineers can log on a
// job (the active NU items with Nano Fibre's "NU Job Stock" switch on) put
// into our own sections, in our own order: that's the layout of "Stock used"
// on the job card. Items in no section show after the sections, under their
// Nano Fibre category. Nothing changes on the job card until it's saved.

const CATEGORY_NAMES = { civil: 'Meters', consumables: 'Consumables', civils: 'Civils', tools: 'Tools', uniform_ppe: 'Uniform & PPE' };
const DRAFT_KEY = 'nu.jobStockLayout.draft';
const readDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY));
    return Array.isArray(d?.sections) ? { sections: d.sections, required: Array.isArray(d.required) ? d.required : [], noReorder: Array.isArray(d.noReorder) ? d.noReorder : null, packs: d.packs && typeof d.packs === 'object' ? d.packs : null } : null;
  } catch { return null; }
};
const writeDraft = (json) => { try { localStorage.setItem(DRAFT_KEY, json); } catch { /* lasts this visit */ } };
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* fine */ } };
// The layout as saved: sections in order, and the required items.
const byId = (a, b) => a - b;
// packs: { itemId: { size, unit } }, items ordered in packs and used singly.
const snapshot = (sections, required, noReorder, packs) => JSON.stringify({ sections: sections.map(({ name, itemIds }) => ({ name, itemIds })), required: [...required].sort(byId), noReorder: [...noReorder].sort(byId), packs: Object.fromEntries(Object.entries(packs).sort(([a], [b]) => a - b)) });
let nextKey = 1;
const keyed = (name, itemIds) => ({ key: nextKey++, name, itemIds });

const move = (list, from, to) => {
  const out = [...list];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x);
  return out;
};

export default function JobStockLayout({ me }) {
  const canEdit = me.capabilities.includes('page.stock-job-layout.edit');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [saved, setSaved] = useState('');
  const [required, setRequired] = useState([]); // item ids every job must log
  const [noReorder, setNoReorder] = useState([]); // orderable item ids NOT added to the next order
  const [packs, setPacks] = useState({}); // { itemId: { size, unit } }
  const [swap, setSwap] = useState(null); // item id picked to swap
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  // The saved layout from the server; with `restore`, unsaved changes kept on
  // this computer (a refresh or leaving the page doesn't lose them) go on top.
  const take = (d, restore = false) => {
    setData(d);
    const s = d.sections.map((x) => keyed(x.name, x.items.map((i) => i.id)));
    const all = [...d.sections.flatMap((x) => x.items), ...d.unplaced];
    const req = all.filter((i) => i.required).map((i) => i.id).sort(byId);
    const off = all.filter((i) => i.orderable && !i.autoOrder).map((i) => i.id).sort(byId);
    const pk = Object.fromEntries(all.filter((i) => i.pack).map((i) => [i.id, { size: String(i.pack.size), unit: i.pack.unit }]));
    setSaved(snapshot(s, req, off, pk));
    const draft = restore ? readDraft() : null;
    if (draft) {
      setSections(draft.sections.map((x) => keyed(x.name, x.itemIds)));
      setRequired(draft.required);
      setNoReorder(draft.noReorder ?? off);
      setPacks(draft.packs ?? pk);
      setNotice({ tone: 'warn', text: 'Your changes from before are back. They aren’t on the job card until you press Save layout.' });
    } else {
      setSections(s);
      setRequired(req);
      setNoReorder(off);
      setPacks(pk);
      clearDraft();
    }
    setSwap(null);
  };
  const load = useCallback((restore = false) => {
    api('/job-stock/layout').then((d) => { take(d, restore); setError(null); }, (e) => setError(problem(e)));
  }, []);
  useEffect(() => { load(true); }, [load]);

  const items = useMemo(() => {
    const all = data ? [...data.sections.flatMap((s) => s.items), ...data.unplaced] : [];
    return new Map(all.map((i) => [i.id, i]));
  }, [data]);
  const plan = sections.map(({ name, itemIds }) => ({ name, itemIds: itemIds.filter((id) => items.has(id)) }));
  const current = snapshot(sections, required, noReorder, packs);
  const dirty = Boolean(data) && current !== saved;
  // Keep unsaved changes on this computer, and ask before closing the tab.
  useEffect(() => {
    if (!data) return undefined;
    if (!dirty) { clearDraft(); return undefined; }
    writeDraft(current);
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [data, dirty, current]);
  const placed = new Set(sections.flatMap((s) => s.itemIds));
  const unplaced = [...items.values()].filter((i) => !placed.has(i.id));

  const isRequired = (id) => required.includes(id);
  const addsToOrders = (id) => Boolean(items.get(id)?.orderable) && !noReorder.includes(id);
  const toggleReorder = (id) => { setNotice(null); setNoReorder((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])); };
  const setPack = (id, patch) => {
    setNotice(null);
    setPacks((cur) => {
      const next = { ...cur, [id]: { size: '', unit: 'units', ...cur[id], ...patch } };
      if (patch.off) delete next[id];
      return next;
    });
  };
  const toggleRequired = (id) => { setNotice(null); setRequired((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])); };

  // What the job card will look like with these sections.
  const preview = [
    ...sections.flatMap((s) => s.itemIds.filter((id) => items.has(id)).map((id) => ({ ...items.get(id), section: { id: s.key, name: s.name || 'Unnamed section' } }))),
    ...unplaced,
  ].map((i) => ({ ...i, required: isRequired(i.id), pack: packs[i.id] ? { size: Number(packs[i.id].size) || 0, unit: packs[i.id].unit } : null }));

  const where = (id) => {
    for (const [si, s] of sections.entries()) {
      const at = s.itemIds.indexOf(id);
      if (at >= 0) return { si, at };
    }
    return null;
  };
  const edit = (fn) => { setNotice(null); setSections((cur) => fn(cur.map((s) => ({ ...s, itemIds: [...s.itemIds] })))); };

  const putIn = (id, target) => edit((cur) => {
    for (const s of cur) s.itemIds = s.itemIds.filter((x) => x !== id);
    if (target !== '') cur.find((s) => s.key === Number(target))?.itemIds.push(id);
    return cur;
  });
  const nudge = (id, d) => edit((cur) => {
    const w = where(id);
    if (!w) return cur;
    const to = w.at + d;
    if (to < 0 || to >= cur[w.si].itemIds.length) return cur;
    cur[w.si].itemIds = move(cur[w.si].itemIds, w.at, to);
    return cur;
  });
  // Swap two items' places (in the same section or across sections). An item
  // swapped with one not placed takes the other's place out of the sections.
  const pick = (id) => {
    if (swap === null) return setSwap(id);
    if (swap === id) return setSwap(null);
    const a = swap;
    setSwap(null);
    return edit((cur) => {
      const wa = where(a);
      const wb = where(id);
      if (wa) cur[wa.si].itemIds[wa.at] = id;
      if (wb) cur[wb.si].itemIds[wb.at] = a;
      return cur;
    });
  };

  const addSection = (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    edit((cur) => [...cur, keyed(name, [])]);
    setNewName('');
  };
  const rename = (key, name) => edit((cur) => cur.map((s) => (s.key === key ? { ...s, name } : s)));
  const shift = (si, d) => edit((cur) => (si + d < 0 || si + d >= cur.length ? cur : move(cur, si, si + d)));
  const drop = (key) => edit((cur) => cur.filter((s) => s.key !== key));
  const fromCategories = () => edit((cur) => {
    const groups = new Map();
    for (const i of unplaced) {
      const name = CATEGORY_NAMES[i.category] || 'Other';
      groups.set(name, [...(groups.get(name) || []), i.id]);
    }
    for (const [name, ids] of groups) {
      const same = cur.find((s) => s.name.toLowerCase() === name.toLowerCase());
      if (same) same.itemIds.push(...ids);
      else cur.push(keyed(name, ids));
    }
    return cur;
  });

  const save = async () => {
    setBusy('save');
    setNotice(null);
    try {
      take(await api('/job-stock/layout', { method: 'PUT', body: { sections: plan, required: required.filter((id) => items.has(id)), noReorder: noReorder.filter((id) => items.has(id)), packs: Object.entries(packs).filter(([id]) => items.has(Number(id))).map(([id, p]) => ({ itemId: Number(id), size: Number(p.size), unit: p.unit })) } }));
      setNotice({ tone: 'ok', text: 'Saved. Engineers see the new layout the next time their job card loads the stock list (within a minute).' });
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(null);
    }
  };
  const refresh = async () => {
    setBusy('copy');
    setNotice(null);
    try {
      const r = await api('/stock/catalogue/refresh', { method: 'POST' });
      const d = await api('/job-stock/layout');
      // Keep unsaved changes; just pick up items switched on or off.
      setData(d);
      setNotice({ tone: 'ok', text: `Copied ${r.items} items from Nano Fibre.` });
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(null);
    }
  };

  const row = (i, s, at, count) => (
    <li key={i.id} className={`jl-item${swap === i.id ? ' picked' : ''}${swap !== null && swap !== i.id ? ' can-swap' : ''}`}>
      <div className="jl-item-text">
        <strong>{i.description}{!canEdit && isRequired(i.id) && <span className="req-tag">Required</span>}</strong>
        <span className="small muted">{i.sku}</span>
        {canEdit ? (
          <span className="jl-pack small">
            <label><input type="checkbox" checked={Boolean(packs[i.id])} onChange={(e) => setPack(i.id, e.target.checked ? { size: '100', unit: 'units' } : { off: true })} /> Comes in packs, used singly</label>
            {packs[i.id] && (
              <>
                <span>Pack of</span>
                <input className="popup-input jl-pack-size" inputMode="numeric" value={packs[i.id].size} onChange={(e) => setPack(i.id, { size: e.target.value.replace(/\D/g, '').slice(0, 5) })} aria-label={`Pack size for ${i.description}`} />
                <input className="popup-input jl-pack-unit" value={packs[i.id].unit} maxLength={30} onChange={(e) => setPack(i.id, { unit: e.target.value })} aria-label={`What engineers count for ${i.description}`} placeholder="tablets" />
              </>
            )}
          </span>
        ) : packs[i.id] && <span className="small pack-note">Packs of {packs[i.id].size} {packs[i.id].unit}, used singly</span>}
      </div>
      {canEdit && (
        <div className="jl-item-tools">
          <label className={`jl-req${isRequired(i.id) ? ' on' : ''}`} title="Engineers must log this on every job before they can complete it">
            <input type="checkbox" checked={isRequired(i.id)} onChange={() => toggleRequired(i.id)} /> Required
          </label>
          <label className={`jl-req jl-order${addsToOrders(i.id) ? ' on' : ''}${i.orderable ? '' : ' disabled'}`} title={i.orderable ? 'When used on a job, it goes onto the team’s next stock order by itself' : 'Nano Fibre doesn’t let us order this item (SKU Manager), so it can’t go on an order'}>
            <input type="checkbox" checked={addsToOrders(i.id)} disabled={!i.orderable} onChange={() => toggleReorder(i.id)} /> Add to orders
          </label>
          {s && <button type="button" className="btn icon-only" aria-label={`Move ${i.description} up`} disabled={at === 0} onClick={() => nudge(i.id, -1)}><ArrowUp size={16} aria-hidden="true" /></button>}
          {s && <button type="button" className="btn icon-only" aria-label={`Move ${i.description} down`} disabled={at === count - 1} onClick={() => nudge(i.id, 1)}><ArrowDown size={16} aria-hidden="true" /></button>}
          <button type="button" className={`btn with-icon jl-swap${swap === i.id ? ' primary' : ''}`} onClick={() => pick(i.id)} aria-pressed={swap === i.id}>
            <ArrowLeftRight size={16} aria-hidden="true" /> {swap === null ? 'Swap' : swap === i.id ? 'Cancel' : 'Swap here'}
          </button>
          <select className="popup-input jl-move" aria-label={`Section for ${i.description}`} value={s ? String(s.key) : ''} onChange={(e) => putIn(i.id, e.target.value)}>
            <option value="">Not in a section</option>
            {sections.map((x) => <option key={x.key} value={x.key}>{x.name || 'Unnamed section'}</option>)}
          </select>
        </div>
      )}
    </li>
  );

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Job card stock organizer</h1>
            <p className="muted">Set how “Stock used” looks on the job card: make your own sections, put the items in them, and order them. Use Swap to swap two items’ places, the arrows to move an item up or down, and the list to move it to another section. Items not in a section show after your sections, under their Nano Fibre category. Tick Required on an item engineers must log on every job: they can’t complete the job without it. Add to orders: what’s used on a job goes onto that team’s next stock order by itself (untick to leave it off). Comes in packs, used singly: engineers log single ones (e.g. tablets) and a pack goes on the order for each pack’s worth used.</p>
            <p className="small muted">The items are the active Nano Utilities items with “NU Job Stock” switched on in Nano Fibre’s SKU Manager, copied every 5 minutes.{data?.updatedAt ? ` Layout last saved ${formatDateTime(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ''}.` : ''}</p>
          </div>
          <div className="head-actions">
            {canEdit && <button type="button" className="btn with-icon" onClick={refresh} disabled={busy !== null}><RefreshCw size={18} aria-hidden="true" /> {busy === 'copy' ? 'Copying…' : 'Copy items now'}</button>}
            {canEdit && dirty && <button type="button" className="btn with-icon" onClick={() => { clearDraft(); load(false); }} disabled={busy !== null}><X size={18} aria-hidden="true" /> Undo changes</button>}
            {canEdit && <button type="button" className="btn primary" onClick={save} disabled={!dirty || busy !== null}>{busy === 'save' ? 'Saving…' : 'Save layout'}</button>}
          </div>
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        {canEdit && dirty && !notice && <p className="notice-line notice-warn" role="status">Unsaved changes: the job card still shows the last saved layout until you press Save layout. They’re kept on this computer if you leave the page.</p>}
        {swap !== null && <p className="notice-line notice-warn" role="status">Now pick the item to swap “{items.get(swap)?.description}” with.</p>}
      </section>

      {error && <section className="card"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></section>}
      {!error && !data && <section className="card"><p className="muted">Loading…</p></section>}

      {data && (
        <div className="jl-grid">
          <div className="stack">
            {sections.map((s, si) => {
              const list = s.itemIds.filter((id) => items.has(id));
              return (
                <section key={s.key} className="card jl-section" aria-label={s.name || 'Unnamed section'}>
                  <div className="jl-section-head">
                    {canEdit
                      ? <input className="popup-input jl-name" value={s.name} maxLength={60} onChange={(e) => rename(s.key, e.target.value)} aria-label="Section name" placeholder="Section name" />
                      : <h2>{s.name}</h2>}
                    {canEdit && (
                      <div className="jl-item-tools">
                        <button type="button" className="btn icon-only" aria-label={`Move section ${s.name} up`} disabled={si === 0} onClick={() => shift(si, -1)}><ArrowUp size={16} aria-hidden="true" /></button>
                        <button type="button" className="btn icon-only" aria-label={`Move section ${s.name} down`} disabled={si === sections.length - 1} onClick={() => shift(si, 1)}><ArrowDown size={16} aria-hidden="true" /></button>
                        <button type="button" className="btn icon-only" aria-label={`Remove section ${s.name}`} title="Remove the section (its items go back to Not in a section)" onClick={() => drop(s.key)}><Trash2 size={16} aria-hidden="true" /></button>
                      </div>
                    )}
                  </div>
                  {list.length
                    ? <ol className="jl-items">{list.map((id, at) => row(items.get(id), s, at, list.length))}</ol>
                    : <p className="small muted">No items yet. Move items here with their section list.</p>}
                </section>
              );
            })}

            {canEdit && (
              <section className="card">
                <form className="jl-add" onSubmit={addSection}>
                  <input className="popup-input" value={newName} maxLength={60} onChange={(e) => setNewName(e.target.value)} placeholder="New section, e.g. Fittings" aria-label="New section name" />
                  <button type="submit" className="btn with-icon" disabled={!newName.trim()}><Plus size={18} aria-hidden="true" /> Add section</button>
                </form>
                {unplaced.length > 0 && <button type="button" className="link-btn" onClick={fromCategories}>Put the items not in a section into sections by Nano Fibre category</button>}
              </section>
            )}

            <section className="card jl-section" aria-label="Not in a section">
              <div className="jl-section-head"><h2>Not in a section</h2><span className="small muted">{unplaced.length} items, shown by category after your sections</span></div>
              {unplaced.length
                ? <ol className="jl-items">{unplaced.map((i) => row(i, null, 0, 0))}</ol>
                : <p className="small muted">Every item is in a section.</p>}
            </section>
          </div>

          <section className="card jl-preview" aria-label="Job card preview">
            <h2>On the job card</h2>
            <p className="small muted">How step 4 “Stock used” will look{dirty ? ' once saved' : ''}.</p>
            <div className="job-stock">
              <ItemGroups items={preview} values={{}} onChange={() => {}} done={() => false} tally={(g) => `${g.length} ${g.length === 1 ? "item" : "items"}`} whole={() => true} verb="How many used:" />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
